import {
  FETCH_USER,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILED,
  USER_SIGN_IN,
  USER_SIGN_IN_FAILED,
  USER_SIGN_OUT,
  CLEAR_LOGIN_ERROR,
  REQUEST_OTP,
  REQUEST_OTP_SUCCESS,
  REQUEST_OTP_FAILED,
  UPDATE_USER_WALLET_HISTORY,
  FETCH_ALL_WALLET_HISTORY,
  FETCH_ALL_WALLET_HISTORY_SUCCESS,
  FETCH_ALL_WALLET_HISTORY_FAILED,
  UPDATE_USER_MILES_HISTORY,
  SEND_RESET_EMAIL,
  SEND_RESET_EMAIL_FAILED
} from "../store/types";

import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import {onValue, update, set, off, ref, query, orderByChild, orderByKey, equalTo, startAt, endAt, get, limitToLast} from "firebase/database";
import { onAuthStateChanged, signInWithCredential, signInWithPopup, signOut, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithCustomToken, reload } from "firebase/auth";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import base64 from 'react-native-base64';
import AccessKey from '../other/AccessKey';
// Bearer-token POST (mileactions imports neither authactions nor its importers: no cycle).
import { postWithIdToken } from './mileactions';

// Platform-specific encryption imports.
// Web uses Web Crypto API (subtle); mobile uses CryptoJS via react-native modules.
// Detect runtime — try/catch on require() does NOT work here because Metro
// can resolve the web-app path from the workspace and silently load it,
// causing mobile to call window.crypto.subtle (undefined on RN) and crash.
const isReactNative = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
let encryptData, decryptData;
if (isReactNative) {
  const mobileEncryption = require('../other/EncryptionUtils');
  encryptData = mobileEncryption.encryptData;
  decryptData = mobileEncryption.decryptData;
} else {
  const webEncryption = require('../../../web-app/src/other/EncryptionUtils');
  encryptData = webEncryption.encryptData;
  decryptData = webEncryption.decryptData;
}

// check_auth_exists creates the users/<uid> record of a signed-in account that
// has none (the first login). The new server takes the uid from the ID token
// and builds the record from the Auth account (it ignores the body). Every
// first login goes through here, so it must work whatever the deploy order:
// when the Bearer request does not come back with a record (an older server
// only knows the shared key and answers "Unauthorized api call"; a 401 is a
// token the server could not verify), the request goes once more exactly as
// before (shared key + the same body), which both servers still accept.
const postCheckAuthExists = async (user, url, data) => {
  const { config } = firebase;
  const body = JSON.stringify({data: JSON.stringify(data)});
  let idToken = null;
  try {
    idToken = await user.getIdToken(false);
  } catch (tokenError) {
    idToken = null;
  }
  if (idToken) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + idToken
      },
      body: body
    });
    const json = await response.json().catch(() => ({}));
    if (json && json.uid) return json;
  }
  const legacy = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
    },
    body: body
  });
  return legacy.json();
};

// Signs in again with the one-time custom token the server returns after an
// account change (same uid, so no auth-state change fires). Best effort: on
// failure the device stays signed in until its current ID token expires.
const adoptReturnedSession = async (token) => {
  if (!token) return false;
  const { auth } = firebase;
  try {
    await signInWithCustomToken(auth, token);
    return true;
  } catch (sessionError) {
    console.warn('[auth] could not adopt the new session token:', (sessionError && sessionError.message) || sessionError);
    return false;
  }
};

// The account endpoints answer every success with success: true. Anything
// else that comes back with HTTP 200 (an older server's { error }, an HTML
// page) is not a success: it is thrown like a refusal, with its code if any.
const requireSuccess = (res) => {
  if (res && res.success === true) return res;
  const err = new Error((res && (res.error || res.message)) || 'Unexpected answer');
  err.code = (res && res.code) || null;
  err.payload = res || {};
  throw err;
};

// One rule for every Ethiopian mobile number, used before every phone call
// (the code request, the sign-in, the phone change) and by the server
// (functions/common/sharedSMS.js normalizeMobileNumber; the parity test
// mobile-app/test/phoneNumber.test.js runs both): keep the digits, drop ONE
// leading 0 (0911 234 567) or a leading 251 / +251 / 00251, then exactly 9
// digits starting with 9 (Ethio Telecom) or 7 (Safaricom). Answers
// '+2519XXXXXXXX' / '+2517XXXXXXXX', or null for anything else ('+2510911…'
// and '+251+251…' are refused, never guessed at). Other countries only with
// opts.allowOtherCountries (settings.AllowCountrySelection): '+', a country
// code and at most 15 digits in all (E.164).
export const normalizeMobileNumber = (value, opts) => {
  const allowOther = !!(opts && opts.allowOtherCountries);
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const raw = String(value).trim();
  if (!raw || /[^0-9+\s\-().]/.test(raw)) return null;
  // One '+', and only in front ('(+251) 91…' is fine, '+251+251…' is not).
  let international = raw.replace(/^[\s(]+/, '').charAt(0) === '+';
  const plusCount = (raw.match(/\+/g) || []).length;
  if (plusCount > 1 || (plusCount === 1 && !international)) return null;
  let digits = raw.replace(/\D/g, '');
  if (!international && digits.indexOf('00') === 0) {
    international = true;
    digits = digits.slice(2);
  }
  let national = null;
  if (international) {
    if (digits.indexOf('251') !== 0) {
      return allowOther && /^[1-9][0-9]{7,14}$/.test(digits) ? '+' + digits : null;
    }
    national = digits.slice(3);
  } else if (digits.length === 12 && digits.indexOf('251') === 0) {
    national = digits.slice(3);
  } else if (digits.length === 10 && digits.charAt(0) === '0') {
    national = digits.slice(1);
  } else if (digits.length === 9) {
    national = digits;
  } else {
    return null;
  }
  return /^[79][0-9]{8}$/.test(national) ? '+251' + national : null;
};

const allowOtherCountries = () => {
  const settings = store.getState().settingsdata.settings;
  return !!(settings && settings.AllowCountrySelection);
};

// Why a code request was refused, for the screens: the server's own `code`
// when it sends one (request_mobile_otp, Oct 2026: invalid_mobile,
// rate_limited, sms_unavailable, server_error), else read from the HTTP status
// and the old server's text, so this also works before that deploy (429 with
// retryAfter, 503 'sms_unavailable', 400, 500 'Setup error').
const otpRequestFailure = (status, body) => {
  const b = body && typeof body === 'object' ? body : {};
  const text = String(b.error || b.message || ('HTTP ' + status));
  const retryAfter = Number(b.retryAfter) > 0 ? Math.ceil(Number(b.retryAfter)) : 0;
  let code = typeof b.code === 'string' && b.code ? b.code : null;
  if (code === 'server_error') code = 'server';
  if (!code) {
    if (status === 429 || /too many/i.test(text)) code = 'rate_limited';
    else if (status === 503 || /sms_unavailable/i.test(text)) code = 'sms_unavailable';
    else if (status === 400) code = 'invalid_mobile';
    else code = 'server';
  }
  return { success: false, code: code, error: text, retryAfter: retryAfter };
};

export const fetchUser = () => (dispatch) => {
  const {
    auth,
    config,
    singleUserRef
  } = firebase;

  dispatch({
    type: FETCH_USER,
    payload: null
  });
  onAuthStateChanged(auth, user => {
    if (user) {
      onValue(singleUserRef(user.uid), async snapshot => {
        let profile = snapshot.val(); // one tree per delivery (P2-1)
        if (profile) {
          profile.uid = user.uid;
          dispatch({
            type: FETCH_USER_SUCCESS,
            payload: profile
          });
        }else{
          let data = {
            uid : user.uid,
            mobile: '',
            email: '',
            firstName: '',
            lastName: '',
            verifyId: ''
          }

          if(user.providerData.length == 0 && user.email){
            data.email = user.email;
          }
          if(user.providerData.length > 0 && user.phoneNumber){
            data.mobile = user.phoneNumber;
          }
          if (user.providerData.length > 0) {
            const provideData = user.providerData[0];
            if (provideData == 'phone') {
              data.mobile = provideData.phoneNumber;
            }
            if (provideData.providerId == "google.com" || provideData.providerId == 'apple.com') {
              if (provideData.email) {
                data.email = provideData.email;
              }
              if (provideData.phoneNumber) {
                data.mobile = provideData.phoneNumber;
              }
              if (provideData.displayName) {
                if (provideData.displayName.split(" ").length > 0) {
                  data.firstName = provideData.displayName.split(" ")[0];
                  data.lastName = provideData.displayName.split(" ")[1];
                } else {
                  data.firstName = provideData.displayName;
                }
              }
              if (provideData.photoURL) {
                data['profile_image'] = provideData.photoURL;
              }
            }
          }
          if(user.providerData.length > 0 && user.verifyId){
            data.verifyId = user.verifyId;
          }

          // Auto-registered phone customers arrive with no name or email
          // from the provider. The booking gate requires firstName, lastName
          // and a valid email, so seed sensible defaults: a placeholder name
          // and an email derived from the verified phone number (digits only,
          // e.g. 251912345678@donkeydrive.io) matching the manual-rider
          // convention used elsewhere in the app.
          if (data.mobile && !data.email) {
            data.email = data.mobile.replace(/\D/g, '') + '@donkeydrive.io';
            if (!data.firstName) data.firstName = 'Manual';
            if (!data.lastName) data.lastName = 'Customer';
          }

          const settings = store.getState().settingsdata.settings;
          let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
          let url = `${host}/check_auth_exists`;
          const json = await postCheckAuthExists(user, url, data);
          if(json.uid && json.existed === true && !json.usertype){
            // The record was created meanwhile by another sign-in path; the
            // server (shared-key answer) sent only its uid, and this listener
            // delivers the record itself.
            return;
          }
          if(json.uid){
            dispatch({
              type: FETCH_USER_SUCCESS,
              payload: json
            });
          } else{
            dispatch({
              type: FETCH_USER_FAILED,
              payload: { code: store.getState().languagedata.defaultLanguage.auth_error, message: json.error }
            });
          }
        }
      });
    } else {
      dispatch({
        type: FETCH_USER_FAILED,
        payload: { code: store.getState().languagedata.defaultLanguage.auth_error, message: store.getState().languagedata.defaultLanguage.not_logged_in }
      });
    }
  });
};

export const validateReferer = async (referralId) => {
  const {
    config
  } = firebase;
  const response = await fetch(`https://${config.projectId}.web.app/validate_referrer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      referralId: referralId
    })
  })
  const json = await response.json();
  return json;
};

export const fetchSingleProfile = async (customerData) => {
  const {
    config
  } = firebase;

  const settings = store.getState().settingsdata.settings;
  let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
  let url = `${host}/check_profile_exists`;

  // Mobile Lookup or Referral ID base lookup
  if (customerData.mobile) {
    const response = await fetch(url, {
    method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
      },
      body: JSON.stringify({
        mobile: customerData.mobile
      })
    })
    const json = await response.json();
    return json;
  } else if (customerData.code) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
      },
      body: JSON.stringify({
        code: customerData.code
      })
    })
    const json = await response.json();
    return json;
  }
};

export const checkUserExists = async (data) => {
  const {
    config
  } = firebase;

  const settings = store.getState().settingsdata.settings;
  let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app` || `http://localhost:3000`
  let url = `${host}/check_user_exists`;
  
  // SECURITY FIX: Encrypt request data for all platforms
  const encryptedPayload = await encryptData({
    email: data.email,
    mobile: data.mobile
  });
  
  // Add encryption version flag to help server identify encrypted requests
  const requestPayload = {
    ...encryptedPayload,
    encryptionVersion: '1.0' // Flag to indicate this is an encrypted request
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
    },
    body: JSON.stringify(requestPayload)
  })
  
  const json = await response.json();

  // SECURITY FIX: Decrypt response data if it's encrypted
  if (json.encrypted && json.payload) {
    const decryptedData = await decryptData(json);
    return decryptedData;
  }
  
  // Backward compatibility: Return non-encrypted response as-is
  return json;
};

export const mainSignUp = async (regData) => {
  const {
    config
  } = firebase;
  let url = `https://${config.projectId}.web.app/user_signup`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ regData: regData })
  })
  const res = await response.json();
  return res;
};

export  const sendWelcomeSms = async (mobile, userCode) => {
  const {
    config
  } = firebase;
  let url = `https://${config.projectId}.web.app/send_signup_welcome_sms`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rawPhone: mobile, userCode: userCode })
  })
  const res = await response.json();
  return res;
};

// Email change (Profile, the book-time name + email capture, My Profile).
// Sent with the signed-in account's ID token: the server changes only the
// caller's own email (another uid needs an admin caller). An email change
// revokes this device's refresh token, so the server answers with a one-time
// sessionToken and the app signs in again with it silently instead of being
// signed out within the hour.
// Resolves { success: true, code: null, error: null, changed } or
// { success: false, code, error }: `code` is the server's (invalid_email,
// email_in_use, forbidden, no_profile, auth_required, auth_invalid,
// session_revoked, server_error; not_signed_in; null = no answer), `error`
// its English text. Fire-and-forget callers keep working.
export const updateProfileWithEmail = (profileData) => async (dispatch) => {
  const body = {
    email: profileData && profileData.email,
    firstName: profileData && profileData.firstName,
    lastName: profileData && profileData.lastName
  };
  // Every caller passes its own uid. Forwarded, so a different uid is judged
  // by the server (admin only) instead of changing the caller's own email.
  if (profileData && typeof profileData.uid === 'string' && profileData.uid) body.uid = profileData.uid;
  try{
    const res = requireSuccess(await postWithIdToken('/update_user_email', body));
    await adoptReturnedSession(res && res.sessionToken);
    return {success: true, code: null, error: null, changed: !!(res && res.changed)};
  }catch(error){
    return {success: false, code: (error && error.code) || null, error: (error && error.message) || String(error)};
  }
}


export const requestPhoneOtpDevice = (verificationId) => async (dispatch) => {
    dispatch({
      type: REQUEST_OTP_SUCCESS,
      payload: verificationId
    }); 
}

export const mobileSignIn = (verficationId, code) => (dispatch) => {
  const {
    auth,
    mobileAuthCredential,
  } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });
  signInWithCredential(auth, mobileAuthCredential(verficationId, code))
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    }).catch(error => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
};

export const googleLogin = (idToken, accessToken) => (dispatch) => {

  const {
    auth,
    googleCredential
  } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });

  const credential = googleCredential(idToken, accessToken);
  signInWithCredential(auth, credential)
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    })
    .catch(error => {
      console.log(error);
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
}

export const appleSignIn = (credentialData) => (dispatch) => {

  const {
    auth,
    appleProvider
  } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });
  if (credentialData) {
    const credential = appleProvider.credential(credentialData);
    signInWithCredential(auth, credential)
      .then((user) => {
        //OnAuthStateChange takes care of Navigation
      })
      .catch((error) => {
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error
        });
      });
  } else {
    signInWithPopup(auth, appleProvider).then(function (result) {
      signInWithCredential(auth, result.credential)
        .then((user) => {
        //OnAuthStateChange takes care of Navigation
        })
        .catch(error => {
          dispatch({
            type: USER_SIGN_IN_FAILED,
            payload: error
          });
        }
        );
    }).catch(function (error) {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
  }
};

export const signOff = () => async (dispatch) => {

  const {
    auth,
    singleUserRef,
    walletHistoryRef,
    userNotificationsRef
  } = firebase;

  const uid = auth.currentUser.uid;

  off(singleUserRef(uid));
  off(walletHistoryRef(uid));
  off(userNotificationsRef(uid));

  const snapshot = await get(singleUserRef(uid));
  if(snapshot.val()){
    const profile = snapshot.val();
    if (profile && profile.usertype === 'driver') {
      // driverOfflineAt: deliberate offline (sign-out) — the server's
      // auto-online pass respects it for autoOnlineRespectManualOfflineMs.
      update(singleUserRef(uid), {driverActiveStatus:false, driverOfflineAt: Date.now()});
    }
    setTimeout(()=>{
      signOut(auth)
      .then(() => {
        dispatch({
          type: USER_SIGN_OUT,
          payload: null
        });
      })
      .catch(error => {

      });
    },2000)
  }
};

export const updateProfile = (updateData) => async (dispatch) => {

  const {
    auth,
    singleUserRef,
    driverDocsRef,
    driverDocsRefBack,
    verifyIdImageRef,
    insuranceImageRef
  } = firebase;

  const uid = updateData.driver_uid ? updateData.driver_uid : auth.currentUser.uid;
  
  if (updateData.licenseImage) {
    await uploadBytesResumable(driverDocsRef(uid), updateData.licenseImage);
    updateData.licenseImage = await getDownloadURL(driverDocsRef(uid));
  }
  if (updateData.licenseImageBack) {
    await uploadBytesResumable(driverDocsRefBack(uid),updateData.licenseImageBack);
    updateData.licenseImageBack = await getDownloadURL(driverDocsRefBack(uid));
  }
  if (updateData.verifyIdImage) {
    await uploadBytesResumable(verifyIdImageRef(uid), updateData.verifyIdImage);
    updateData.verifyIdImage = await getDownloadURL(verifyIdImageRef(uid));
  }
  if (updateData.insuranceImage) {
    await uploadBytesResumable(insuranceImageRef(uid), updateData.insuranceImage);
    updateData.insuranceImage = await getDownloadURL(insuranceImageRef(uid));
  }
  updateData.driver_uid = null;

  update(singleUserRef(uid), updateData);
};


export const updateProfileImage = (imageBlob) => {

  const {
    auth,
    singleUserRef,
    profileImageRef,
  } = firebase;

  const uid = auth.currentUser.uid;

  uploadBytesResumable( profileImageRef(uid), imageBlob).then(() => {
    imageBlob.close()
    return getDownloadURL(profileImageRef(uid))
  }).then((url) => {
    update(singleUserRef(uid), {
      profile_image: url
    });
  })
};

export const updateWebProfileImage = async (imageBlob) => {

  const {
    auth,
    singleUserRef,
    profileImageRef
  } = firebase;

  const uid = auth.currentUser.uid;

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), {profile_image: image});

};
export const updateCustomerProfileImage = async (imageBlob, id) => {

  const {
    singleUserRef,
    profileImageRef
  } = firebase;

  const uid = id

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), {profile_image: image});

};

export const updatePushToken = (token, platform)  => {

  const {
    auth,
    singleUserRef,
  } = firebase;

  const uid = auth.currentUser.uid;

  update(singleUserRef(uid), {
    pushToken: token,
    userPlatform: platform
  });
};

export const clearLoginError = () => (dispatch) => {
  dispatch({
    type: CLEAR_LOGIN_ERROR,
    payload: null
  });
};

export const fetchWalletHistory = () => (dispatch) => {
  const {
    auth,
    walletHistoryRef
  } = firebase;

  // Cold start: Firebase Auth restores the session asynchronously, so a
  // screen mounting immediately after launch can call this before
  // currentUser exists. Bail — callers re-dispatch once the profile loads.
  if (!auth.currentUser) return;
  const uid = auth.currentUser.uid;

  off(walletHistoryRef(uid));
  onValue(walletHistoryRef(uid) , snapshot => {
    const data = snapshot.val();
    if(data){
      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse()
      });
    } else {
      // Resolve the "no history" case explicitly so consumers can tell
      // "loaded, empty" (= []) apart from "still loading" (= null/undefined).
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: []
      });
    }
  });
};

export const fetchWalletHistoryPromise = () => async (dispatch) => {
  const {
    auth,
    walletHistoryRef
  } = firebase;
  if (!auth.currentUser) return;
  const uid = auth.currentUser.uid;
  try {
    const snapshot = await get(walletHistoryRef(uid));
    const data = snapshot.val();
    if (data) {
      const arr = Object.keys(data).map(i => {
        data[i].id = i;
        return data[i];
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse()
      });
      return arr.reverse();
    } else {
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: []
      });
      return [];
    }
  } catch (error) {
    throw error;
  }
};

export const fetchUserWalletHistory = (userId) => async (dispatch) => {
  const {
    walletHistoryRef
  } = firebase;

  const uid = userId;

  try {
    const snapshot = await get(walletHistoryRef(uid));
    const data = snapshot.val();
    if(data){
      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse()
      });
    }else{
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload:[]
      });
    }
  } catch (error) {
    console.log('Error fetching user wallet history:', error);
  }
};

export const fetchUserMilesHistory = (userId) => async (dispatch) => {
  const {
    donkeyMilesHistoryRef
  } = firebase;

  const uid = userId;

  try {
    const snapshot = await get(donkeyMilesHistoryRef(uid));
    const data = snapshot.val();
    if(data){
      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });
      dispatch({
        type: UPDATE_USER_MILES_HISTORY,
        payload: arr.reverse()
      });
    }else{
      dispatch({
        type: UPDATE_USER_MILES_HISTORY,
        payload:[]
      });
    }
  } catch (error) {
    console.log('Error fetching user miles history:', error);
    // Clear rather than silently keeping the previously viewed user's
    // history on screen (e.g. a permission_denied for a role outside the
    // donkeyMilesHistory read rule).
    dispatch({
      type: UPDATE_USER_MILES_HISTORY,
      payload: []
    });
  }
};

// Fetch wallet transactions for the admin TransactionLogs view.
// Previously this read the entire /walletHistory tree (~7 MB per call).
// Now we fan out per-driver with limitToLast so each call costs only
// (driverCount * limit * ~150 B) regardless of historical depth, and we
// skip drivers entirely if Redux already has fresh user state.
const WALLET_HISTORY_PER_DRIVER_LIMIT = 200;

export const fetchAllWalletHistory = (limitPerDriver = WALLET_HISTORY_PER_DRIVER_LIMIT) => async (dispatch, getState) => {
  const { database } = firebase;

  dispatch({ type: FETCH_ALL_WALLET_HISTORY, payload: null });

  try {
    const state = getState ? getState() : null;
    const staticusers = (state && state.usersdata && state.usersdata.staticusers) || [];
    const driverIds = staticusers
      .filter((u) => u && u.usertype === 'driver')
      .map((u) => u.id);

    if (driverIds.length === 0) {
      dispatch({ type: FETCH_ALL_WALLET_HISTORY_SUCCESS, payload: {} });
      return;
    }

    const snapshots = await Promise.all(
      driverIds.map((uid) =>
        get(query(ref(database, 'walletHistory/' + uid), limitToLast(limitPerDriver)))
      )
    );

    const data = {};
    driverIds.forEach((uid, i) => {
      const val = snapshots[i].val();
      if (val) data[uid] = val;
    });

    dispatch({ type: FETCH_ALL_WALLET_HISTORY_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: FETCH_ALL_WALLET_HISTORY_FAILED, payload: error.message });
  }
};

export const clearAllWalletHistory = () => (dispatch) => {
  // Reset to the initial-mount state (payload: null, loading: false)
  // rather than payload: {}. This matters for the consumer's loading
  // gate: an empty object is indistinguishable from "freshly fetched
  // but no records exist", which causes the table to flash "No Records
  // To Display" on remount before the next fetch fires. Null cleanly
  // means "no data yet, expect a fetch".
  dispatch({
    type: FETCH_ALL_WALLET_HISTORY_SUCCESS,
    payload: null
  });
};

// Read aggregated transaction totals from /transactionStats. Used by the
// admin TransactionLogs view to render the Total Amount badge without
// having to scan every wallet history entry — the underlying snapshots
// are maintained at write time by `bumpTransactionStat` in
// functions/common/index.js.
//
// Range semantics:
//   • startKey + endKey present  → single ranged query, cheap
//   • startKey or endKey only    → open-ended range
//   • both null                  → fetch the whole node (all time)
//
// Returns:
//   {
//     total: number,                       // sum of all Credit + Debit
//     byType:   { Credit: n, Debit: n, … },
//     bySource: { regular_booking: { Credit: n, Debit: n }, … },
//     days:     number                     // count of date buckets matched
//   }
//
// Not Redux-dispatched — the caller awaits the Promise. Lightweight
// enough (a few KB) that local component state is cleaner than a slice.
export const fetchTransactionStats = (startKey, endKey) => async () => {
  const { database } = firebase;
  const baseRef = ref(database, 'transactionStats');

  let snap;
  try {
    if (startKey && endKey) {
      snap = await get(query(baseRef, orderByKey(), startAt(startKey), endAt(endKey)));
    } else if (startKey) {
      snap = await get(query(baseRef, orderByKey(), startAt(startKey)));
    } else if (endKey) {
      snap = await get(query(baseRef, orderByKey(), endAt(endKey)));
    } else {
      snap = await get(baseRef);
    }
  } catch (err) {
    console.log('[fetchTransactionStats] failed:', err && err.message);
    return { total: 0, byType: {}, bySource: {}, days: 0, error: err && err.message };
  }

  const val = snap.val() || {};
  // `byDay` preserves the per-day breakdown for the Finance Dashboard's
  // trend chart and spike detection — existing callers that only read
  // total/byType ignore it and pay no extra cost (same single fetch).
  const totals = { total: 0, byType: {}, bySource: {}, byDay: {}, days: 0 };
  Object.keys(val).forEach((dateKey) => {
    const dayData = val[dateKey];
    if (!dayData || typeof dayData !== 'object') return;
    totals.days += 1;
    totals.byDay[dateKey] = dayData;
    Object.keys(dayData).forEach((source) => {
      const sourceData = dayData[source];
      if (!sourceData || typeof sourceData !== 'object') return;
      if (!totals.bySource[source]) totals.bySource[source] = {};
      Object.keys(sourceData).forEach((type) => {
        const amt = Number(sourceData[type]) || 0;
        totals.byType[type] = (totals.byType[type] || 0) + amt;
        totals.bySource[source][type] = (totals.bySource[source][type] || 0) + amt;
        totals.total += amt;
      });
    });
  });
  return totals;
};

export const sendResetMail = (email) => async (dispatch) => {
  const {
    authRef
  } = firebase;

  dispatch({
    type: SEND_RESET_EMAIL,
    payload: email
  });
  sendPasswordResetEmail(authRef(), email).then(function() {
    console.log('Email send successfully');
  }).catch(function (error) {
      dispatch({
        type: SEND_RESET_EMAIL_FAILED,
        payload: {code: store.getState().languagedata.defaultLanguage.auth_error, message: store.getState().languagedata.defaultLanguage.not_registred }
      });
  });
};

export const verifyEmailPassword = (email, pass) => async (dispatch) => {
  const {
    authRef
  } = firebase;

  signInWithEmailAndPassword(authRef(), email, pass)
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    })
    .catch((error) => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
}

// Asks the server to text a sign-in / phone-change code. The Redux side is as
// before (REQUEST_OTP, then REQUEST_OTP_SUCCESS, or REQUEST_OTP_FAILED with
// the server's sentence; nothing when no answer came), so Login and the portal
// keep working. It now also resolves the outcome:
//   { success: true, code: null, error: null, retryAfter: 0 }
//   { success: false, code, error, retryAfter }  code: rate_limited (retryAfter
//     = seconds to wait), sms_unavailable, invalid_mobile, network (no answer),
//     timeout (opts.timeoutMs passed with no answer: the SMS may still come),
//     server (anything else).
// The number goes through normalizeMobileNumber first; a number the rule does
// not know (a test-login number) is sent as given and the server decides.
export const requestMobileOtp = (mobile, opts) => async (dispatch) => {
  const {
    config
  } = firebase;
  dispatch({
    type: REQUEST_OTP,
    payload: true
  });

  const settings = store.getState().settingsdata.settings;
  let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
  let url = `${host}/request_mobile_otp`;
  const sendMobile = normalizeMobileNumber(mobile, { allowOtherCountries: allowOtherCountries() }) || mobile;
  const send = async () => {
    let response;
    try{
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobile: sendMobile })
      });
    }catch(error){
      console.log(error);
      return { success: false, code: 'network', error: (error && error.message) || 'Network request failed', retryAfter: 0 };
    }
    let result;
    try{
      result = await response.json();
    }catch(error){
      console.log(error);
      return otpRequestFailure(response.status, null);
    }
    if(result && result.success){
      dispatch({
        type: REQUEST_OTP_SUCCESS,
        payload: true
      });
      return { success: true, code: null, error: null, retryAfter: 0 };
    }
    dispatch({
      type: REQUEST_OTP_FAILED,
      payload: result && result.error
    });
    return otpRequestFailure(response.status, result);
  };
  const timeoutMs = opts && Number(opts.timeoutMs) > 0 ? Number(opts.timeoutMs) : 0;
  if (!timeoutMs) return send();
  // React Native's fetch has no timeout of its own on Android. The request
  // goes on after a timeout (its answer still reaches Redux), but the caller
  // hears 'timeout' and must not ask again at once: the SMS may still come.
  let timer = null;
  try {
    return await Promise.race([
      send(),
      new Promise((resolve) => { timer = setTimeout(() => resolve({ success: false, code: 'timeout', error: 'No answer in time', retryAfter: 0 }), timeoutMs); })
    ]);
  } finally {
    clearTimeout(timer);
  }
}

// The signed-in account's own Auth phone number, fresh from Firebase, or null.
// check_user_exists only says whether a number has an account; when that
// account is the caller's (an earlier change finished on the Auth side only),
// the number is not taken.
export const ownAuthMobile = async () => {
  const { auth } = firebase;
  const user = auth && auth.currentUser;
  if (!user) return null;
  try {
    await reload(user);
  } catch (reloadError) {
    // keep the cached value
  }
  const now = auth.currentUser || user;
  return (now && now.phoneNumber) || null;
};

export const verifyMobileOtp = (mobile, otp, usertype) => async (dispatch) => {
  const {
    auth,
    config
  } = firebase;
  const body = {
    // The same rule the code request used, so both name the same number.
    mobile: normalizeMobileNumber(mobile, { allowOtherCountries: allowOtherCountries() }) || mobile,
    otp: otp,
    usertype: usertype
  };
  try{
    const settings = store.getState().settingsdata.settings;
    let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
    let url = `${host}/verify_mobile_otp`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    })
    const result = await response.json();
    if(result.token){
      signInWithCustomToken(auth,result.token)
        .then((user) => {
          //OnAuthStateChange takes care of Navigation
        })
        .catch((error) => {
          dispatch({
            type: USER_SIGN_IN_FAILED,
            payload: error
          });
        });
    }else{
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: result.error
      });
    }
  }catch(error){
    console.log(error);
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: error
    });
  }
}

// Phone number change after the OTP. Sent with the signed-in account's ID
// token AND its uid: the server from the wallet security change set takes the
// account from the token (a uid that is not the caller's is a 403), the one
// deployed before it reads body.uid, so the change works whichever is live.
// The number goes through normalizeMobileNumber (the rule the code request
// used). The new server writes users/<uid>/mobile itself and answers with it
// (res.mobile); the old one changes only the Auth phone, so the profile gets
// the number here, alone ({mobile, email, tin} with an undefined field makes
// update() throw), and Auth and the profile never disagree. A success also
// brings a one-time sessionToken, adopted like the email change's.
// Resolves { success: true, code: null, error: null, mobile } or
// { success: false, code, error }: `error` keeps the strings the screens
// already map ('Error updating user' = number in use, anything else = OTP not
// valid); `code` is the server's (otp_mismatch, otp_expired,
// otp_not_requested, otp_attempts_exceeded, mobile_in_use, invalid_mobile,
// auth_required, auth_invalid, session_revoked, server_error; not_signed_in;
// null = no code: an older server, or no answer).
export const updateAuthMobile = async ( mobile, otp) => {
  const { auth, singleUserRef } = firebase;
  const sendMobile = normalizeMobileNumber(mobile, { allowOtherCountries: allowOtherCountries() }) || mobile;
  const uid = auth && auth.currentUser ? auth.currentUser.uid : null;
  const body = { mobile: sendMobile, otp: otp };
  if (uid) body.uid = uid;
  try{
    const res = requireSuccess(await postWithIdToken('/update_auth_mobile', body));
    const serverWrote = !!(res && typeof res.mobile === 'string' && res.mobile);
    if (!serverWrote && uid) {
      try {
        await update(singleUserRef(uid), { mobile: sendMobile });
      } catch (writeError) {
        console.warn('[auth] the profile number was not updated:', (writeError && writeError.message) || writeError);
      }
    }
    await adoptReturnedSession(res && res.sessionToken);
    return {success: true, code: null, error: null, mobile: serverWrote ? res.mobile : sendMobile};
  }catch(error){
    return {success: false, code: (error && error.code) || null, error: (error && error.payload && error.payload.error) || (error && error.message) || String(error)};
  }
}

// Settings > Convert (customer <-> driver), decided on the server
// (convert_user_role): it remembers each role's approval, so a switch never
// raises `approved` (an un-approved driver stays un-approved after
// driver -> customer -> driver). to: 'customer' | 'driver'.
// A plain async function, not a thunk: `await api.convertUserRole(to)`.
// Resolves { success: true, code: null, error: null, usertype, approved,
// unchanged } or { success: false, code, error } (code: invalid_role,
// no_profile, not_allowed, auth_required, auth_invalid, session_revoked,
// server_error; not_signed_in; null = no answer). The profile listener
// (fetchUser) delivers the new usertype.
export const convertUserRole = async (to) => {
  try{
    const res = requireSuccess(await postWithIdToken('/convert_user_role', { to: to }));
    return {success: true, code: null, error: null, usertype: res.usertype, approved: res.approved === true, unchanged: res.unchanged === true};
  }catch(error){
    return {success: false, code: (error && error.code) || null, error: (error && error.message) || String(error)};
  }
}
